import { useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmployeeCalendarEvent,
  EmployeeCalendarEventColors,
  EmployeeCalendarEventLegend,
  EmployeeCalendarProps,
} from '~/components/EmployeeCalendar';
import {
  SYSTEM_PUBLIC_HOLIDAY_EVENT,
  absenceEventColors,
} from '~/components/EmployeeCalendar/Absences/data';

type PropsType = {
  events: EmployeeCalendarEvent[];
};

type UsePublicAbsenceCalendarConfigOutput = Pick<
  EmployeeCalendarProps,
  'events' | 'getEventColors' | 'getEventContent' | 'legends'
>;

export const usePublicAbsenceCalendarConfig = (
  props: PropsType,
): UsePublicAbsenceCalendarConfigOutput => {
  const { events } = props;

  const theme = useTheme();

  const { t } = useTranslation();

  const { legendsByName, legends } = useMemo(() => {
    const publicHolidayLegend: EmployeeCalendarEventLegend = {
      key: 'publicHoliday',
      content: 'PH',
      label: t('publicHoliday'),
      colors: {
        backgroundColor: absenceEventColors.PUBLIC_HOLIDAY,
        color: theme.palette.common.white,
      },
    };

    const otherLegend = {
      key: 'other',
      content: '',
      label: t('laVacation'),
      colors: {
        backgroundColor: absenceEventColors.HOLIDAY_GREEN,
        color: theme.palette.common.white,
      },
    };

    return {
      legendsByName: {
        publicHolidayLegend,
        otherLegend,
      },
      legends: [publicHolidayLegend, otherLegend],
    };
  }, [t, theme]);

  const getEventColors = useCallback(
    (event: EmployeeCalendarEvent): EmployeeCalendarEventColors | undefined => {
      if (event.eventCategory === SYSTEM_PUBLIC_HOLIDAY_EVENT) {
        return legendsByName.publicHolidayLegend.colors;
      }

      return legendsByName.otherLegend.colors;
    },
    [legendsByName],
  );

  const getEventContent = useCallback(
    (event: EmployeeCalendarEvent): string => {
      if (event.eventCategory === SYSTEM_PUBLIC_HOLIDAY_EVENT) {
        return legendsByName.publicHolidayLegend.content;
      }

      return legendsByName.otherLegend.content;
    },
    [legendsByName],
  );

  return {
    events,
    getEventColors,
    getEventContent,
    legends,
  };
};
